import { getCalendarEventsForDateRange } from './getCalendarEventsForDateRange';
import { calendarEventsInDateRangeLoadedForLock } from '../../actions/eventsCacheActions';
import { loadingCalendarEvents } from '../../actions/publicActions';
import { MAX_RETRIES_TO_LOAD_EVENTS_FOR_DATERANGE } from '../../constants';
import { CalendarEventsLoadingState } from '../../types/CalendarEventsLoadingState';
import { CalendarId, getCalendarIdByFolderId, getDefaultCalendar } from 'owa-calendar-cache';
import { logCacheUpdateForDiagnosticsAsync } from 'owa-calendar-cache-diagnostics';
import { lazyUpdateCacheWithEventsFromRest } from 'owa-calendar-events-store';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { containsDateRange, DateRange, dateRangesOverlap } from 'owa-datetime-utils';
import { EventsCacheLockId, getEventsFromCache } from 'owa-events-cache';
import { isFeatureEnabled } from 'owa-feature-flags';
import { trace } from 'owa-trace';
import {
    loadCalendarEventsInDateRange,
    LoadingStateChangedCallback,
} from '../../services/loadCalendarEventsInDateRange';
import {
    getEventsCacheLockInfo,
    getAllLockedDateRanges,
    getEventsCache,
} from '../../selectors/eventsCacheSelectors';

interface FetchingCalendarEventsInfo {
    dateRange: DateRange;
    requestPromise: Promise<CalendarEvent[]>;
    lockIds: string[];
}

const fetchingCalendarEventsInfoMap: { [folderId: string]: FetchingCalendarEventsInfo[] } = {};

function tryInitializeInfoList(folderId: string) {
    if (!fetchingCalendarEventsInfoMap[folderId]) {
        fetchingCalendarEventsInfoMap[folderId] = [];
    }
}

function findFetchingCalendarEventsInfo(
    folderId: string,
    dateRange: DateRange
): { index: -1; info: null } | { index: number; info: FetchingCalendarEventsInfo } {
    tryInitializeInfoList(folderId);

    const infoList = fetchingCalendarEventsInfoMap[folderId];
    for (let i = 0; i < infoList.length; i++) {
        if (containsDateRange(infoList[i].dateRange, dateRange, true /* inclusive */)) {
            return { index: i, info: infoList[i] };
        }
    }

    return { index: -1, info: null };
}

function startedFetchingCalendarEvents(
    folderId: string,
    dateRange: DateRange,
    fetchPromise: Promise<CalendarEvent[]>
) {
    const { index, info } = findFetchingCalendarEventsInfo(folderId, dateRange);
    if (index !== -1) {
        info.requestPromise = fetchPromise;
    }
}

function beforeStartFetchingCalendarEvents(lockId: string, folderId: string, dateRange: DateRange) {
    tryInitializeInfoList(folderId);

    fetchingCalendarEventsInfoMap[folderId].push({
        dateRange: dateRange,
        requestPromise: null,
        lockIds: [lockId],
    });
}

function endedFetchingCalendarEvents(folderId: string, dateRange: DateRange) {
    const { index } = findFetchingCalendarEventsInfo(folderId, dateRange);

    if (index !== -1) {
        fetchingCalendarEventsInfoMap[folderId].splice(index, 1);
    }
}

export function getDateRangesBeingFetchedForFolder(folderId: string) {
    tryInitializeInfoList(folderId);

    return fetchingCalendarEventsInfoMap[folderId].map(info => info.dateRange);
}

export function fetchCalendarEventsForDateRange(
    lockId: EventsCacheLockId,
    folderId: string,
    dateRange: DateRange
): { fetchPromise: Promise<CalendarEvent[]>; events: CalendarEvent[] | null } {
    const lockInfo = getEventsCacheLockInfo(lockId, folderId);
    if (dateRange === null) {
        // TODO VSO 61683: Investigate fetchCalendarEventsForDateRange getting called with null date range
        trace.error('[fetchCalendarEventsForDateRange] dateRange parameter should not be null');
    }
    if (
        !lockInfo ||
        !lockInfo.lockedDateRange ||
        !containsDateRange(lockInfo.lockedDateRange, dateRange, true /* inclusive */)
    ) {
        const eventsFromCache = getEventsInDateRangeFromCacheIfExists(folderId, dateRange);

        if (eventsFromCache) {
            // Events for the requested daterange were present in the cache from another lock
            const promise = handleCalendarEventsLoaded(
                lockId,
                folderId,
                dateRange,
                Promise.resolve(eventsFromCache),
                'existing',
                false /* fetchPropertiesFromREST */
            );
            promise.then(result => {
                const calendarId = getCalendarIdByFolderId(folderId);
                loadingCalendarEvents(lockId, calendarId, 'Loaded');
            });
            return { fetchPromise: promise, events: eventsFromCache };
        }

        // If the lock does not have any events date range locked or the range
        // doesn't contain the requested date range or if there are no other locks
        // which are a superset of requested range, then fetch more from server.

        const { info: currentFetchingCalendarEventsInfo } = findFetchingCalendarEventsInfo(
            folderId,
            dateRange
        );

        if (currentFetchingCalendarEventsInfo?.requestPromise) {
            // If there is already a request going for the same date range
            // then return the same request
            // add the lockId to the list of locks that are currently requesting info for the
            // folderId/ date range
            currentFetchingCalendarEventsInfo.lockIds.push(lockId);
            const promise = handleCalendarEventsLoaded(
                lockId,
                folderId,
                dateRange,
                currentFetchingCalendarEventsInfo.requestPromise.then(events =>
                    // We filter the events to only ones that overlap with the requested
                    // date range because the existing request might have date range
                    // equal or bigger than the currently one existed.
                    events
                        ? events.filter(
                              ({ Start, End }) =>
                                  dateRangesOverlap(
                                      dateRange,
                                      { start: Start, end: End },
                                      true /* inclusive */
                                  ) == 0
                          )
                        : []
                ),
                'new',
                false /* fetchPropertiesFromREST */
            );
            return { fetchPromise: promise, events: null };
        }
        // Add the data to the request info map before fetching, because we need this data to be populated before
        // onLoadStateChanged is triggered
        beforeStartFetchingCalendarEvents(lockId, folderId, dateRange);
        const calendarEventsFetchPromise = fetchCalendarEventsFromServer(
            folderId,
            dateRange,
            onLoadStateChanged(folderId, dateRange)
        );
        // Add the promise to the request info map
        startedFetchingCalendarEvents(folderId, dateRange, calendarEventsFetchPromise);

        const promise = handleCalendarEventsLoaded(
            lockId,
            folderId,
            dateRange,
            calendarEventsFetchPromise,
            'new',
            true /* fetchPropertiesFromREST */
        );
        return { fetchPromise: promise, events: null };
    } else {
        // The date range requested already exists in the cache so return that
        const calendarId = getCalendarIdByFolderId(folderId);
        loadingCalendarEvents(lockId, calendarId, 'Loaded');
        const eventsForDateRange = getCalendarEventsForDateRange(lockId, folderId, dateRange);
        return { fetchPromise: Promise.resolve(eventsForDateRange), events: eventsForDateRange };
    }
}

function onLoadStateChanged(folderId: string, dateRange: DateRange) {
    return (calendarId: CalendarId, loadingState: CalendarEventsLoadingState) => {
        const { index, info } = findFetchingCalendarEventsInfo(folderId, dateRange);

        if (index !== -1) {
            // dispatch a loadingCalendarEvents actions for each lock that has requested data to
            // notify them of load state change
            info.lockIds.forEach(lockId => {
                loadingCalendarEvents(lockId, calendarId, loadingState);
            });
        }
    };
}

function fetchCalendarEventsFromServer(
    folderId: string,
    dateRange: DateRange,
    onLoadStateChangedCallback: LoadingStateChangedCallback
) {
    const fetchPromise = (async () => {
        try {
            return await loadCalendarEventsInDateRange(
                dateRange,
                folderId,
                onLoadStateChangedCallback,
                MAX_RETRIES_TO_LOAD_EVENTS_FOR_DATERANGE
            );
        } finally {
            endedFetchingCalendarEvents(folderId, dateRange);
        }
    })();
    return fetchPromise;
}

async function handleCalendarEventsLoaded(
    lockId: EventsCacheLockId,
    folderId: string,
    dateRange: DateRange,
    fetchPromise: Promise<CalendarEvent[]>,
    diagnosticMessageSuffix: 'existing' | 'new',
    fetchPropertiesFromREST: boolean
): Promise<CalendarEvent[]> {
    const result = await fetchPromise;

    // Log to diagnostics panel
    const diagnosticMessageItemsLength = result ? result.length : 'no';
    logCacheUpdateForDiagnosticsAsync({
        lockId: lockId,
        updateMessage: `Fetched ${diagnosticMessageItemsLength} ${diagnosticMessageSuffix} items for date range: ${dateRange.start} - ${dateRange.end}`,
    });

    if (result) {
        calendarEventsInDateRangeLoadedForLock(lockId, folderId, dateRange, result);

        // Cleanup GetCalendarView++ once shadow mailbox is supported
        // https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/62892
        if (isFeatureEnabled('cal-mf-getCalendarViewREST') && fetchPropertiesFromREST) {
            // Only fetching calendarView++ events for primary calendar until below support is added
            // https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/62868
            if (folderId == getDefaultCalendar()?.calculatedFolderId) {
                lazyUpdateCacheWithEventsFromRest.importAndExecute(lockId, folderId, dateRange);
            }
        }
    }

    return result;
}

function getEventsInDateRangeFromCacheIfExists(folderId: string, dateRange: DateRange) {
    const allDateRanges = getAllLockedDateRanges(folderId);
    const cache = getEventsCache(folderId);
    const dateRangePresentInCache = allDateRanges.some(dateRangeEntry =>
        containsDateRange(dateRangeEntry, dateRange, true)
    );

    if (dateRangePresentInCache && cache) {
        return (
            // return a copy of the events here, so that exists events are not modified
            getEventsFromCache(dateRange, cache.events).map(event => ({ ...event }))
        );
    } else {
        return null;
    }
}
